import * as THREE from '../three'

import { Location } from '../../core/Location'

import { Chunk } from '../extras/Chunk'

import { System } from './System'
import { decodeChunk } from '../../core/chunkEncoder'
import { createAtlasBuilder } from '../../core/createAtlasBuilder'
import { createTerrainManager } from '../../core/createTerrainManager'

import { behaviors } from '../../core/behaviors'
import { Directions } from '../../core/chunks'
import { PlacerContext } from '../../core/PlacerContext'
import { BlockState } from '../../core/BlockState'

const v1 = new THREE.Vector3()
const loc1 = new Location()
const bs1 = new BlockState()
const bs2 = new BlockState()

export class ChunksSystem extends System {
  constructor(client) {
    super(client)
    this.chunks = null
    this.placerCtx = new PlacerContext()
    this.behaviorWorldApi = {
      getBlockAtLocation: this.getBlockAtLocation,
    }
    this.waitingForChunkBuild = {} // chunkId => [...resolves]
  }

  start() {
    this.atlas = createAtlasBuilder({
      anisotropy: this.client.graphics.maxAnisotropy,
      skyUniforms: this.client.sky.sky.uniforms,
      registry: this.client.registry,
    })
    this.terrain = createTerrainManager({
      scene: this.client.graphics.scene,
      material: this.atlas.material.base,
      camera: this.client.graphics.camera,
      registry: this.client.registry,
      onBuild: chunkId => {
        const chunk = this.chunks.get(chunkId)
        if (chunk) chunk.built = true
        if (this.waitingForChunkBuild[chunkId]) {
          const resolves = this.waitingForChunkBuild[chunkId]
          for (const resolve of resolves) {
            resolve()
          }
          delete this.waitingForChunkBuild[chunkId]
        }
      },
    })
  }

  get(id) {
    return this.chunks?.get(id)
  }

  waitForChunkAtPosition(x, y, z) {
    loc1.world.set(x, y, z)
    const chunk = this.chunks.get(loc1.chunkId)
    if (chunk?.built) return
    const promise = new Promise(resolve => {
      if (!this.waitingForChunkBuild[loc1.chunkId]) {
        this.waitingForChunkBuild[loc1.chunkId] = []
      }
      this.waitingForChunkBuild[loc1.chunkId].push(resolve)
    })
    return promise
  }

  setup(chunks) {
    // todo: destroy any existing atlas, blocks, chunks etc
    this.chunks = new Map()
    chunks.forEach(data => {
      this.add(data)
    })
  }

  // ensureItem(item) {
  //   throw new Error('TODO: chunk system ensureItem should do a registry check + add')
  //   const added = this.blocks.add(item)
  //   if (added) {
  //     this.sendToChunkWorker('addItem', { item })
  //     this.sendToAtlasWorker('addItem', { item })
  //   }
  // }

  add(data) {
    data.index = decodeChunk(data.indexEncoded)
    const chunk = new Chunk(this.client, data)
    this.chunks.set(chunk.id, chunk)
  }

  rebuild(data) {
    this.remove(data.id)
    this.add(data)
  }

  remove(id) {
    const chunk = this.chunks.get(id)
    if (!chunk) return console.warn('tried to remove chunk that didnt exist')
    this.chunks.delete(id)
    chunk.destroy()
  }

  // preTick(delta) {
  //   this.lastWorkerTick += delta
  //   if (this.lastWorkerTick > WORKER_TICK) {
  //     this.lastWorkerTick -= WORKER_TICK
  //     const camera = this.client.graphics.camera
  //     // camera.updateMatrixWorld(true)
  //     frustumProjMat.multiplyMatrices(camera.projectionMatrix, camera.matrixWorldInverse)
  //     frustum.setFromProjectionMatrix(frustumProjMat)
  //     serializeFrustum(frustum, frustumData)
  //     this.sendToChunkWorker('tick', { frustumData })
  //   }
  // }

  preTick(delta) {
    this.terrain?.update(delta)
  }

  hitBlockAt(loc, amount) {
    const chunk = this.chunks.get(loc.chunkId)
    if (!chunk) return
    let total = chunk.getDamageAt(loc)
    total += amount
    // console.log('hitBlockAt', loc.voxelId, total)
    chunk.setDamageAt(loc, total)
    this.client.network.send('playerHitBlock', {
      voxel: loc.voxel.toArray(),
      amount,
    })
  }

  // breakBlockFromHit(hit, placerObj) {
  //   // cant break if already air
  //   if (hit.block.id === 'air') return
  //   // get location
  //   loc1.voxel.copy(hit.voxel)
  //   // break
  //   this.breakBlockAt(loc1)
  // }

  editorBreakBlockAt(loc) {
    // modify local data immediately so neighbors "see" it
    const chunk = this.chunks.get(loc.chunkId)
    if (!chunk) return
    chunk.setStateAtIdx(loc.voxelIdx, 'air')
    // collect all changes to send to builder
    const changes = []
    changes.push({ loc: loc.voxel.toArray(), state: 'air' })
    // check neighbors for non-destructive changes (recursive)
    const checkNeighbors = loc => {
      for (const dir of Directions.all) {
        const nLoc = loc[dir.name]
        const nChunk = this.chunks.get(nLoc.chunkId)
        if (!nChunk) continue
        const nBlock = nChunk.getBlockAtIdx(nLoc.voxelIdx)
        if (!nBlock) continue
        const currState = bs1.fromString(nBlock.state)
        const nextState = bs2.fromString(nBlock.state)
        const ctx = {
          location: nLoc,
        }
        // allow behaviors a chance to adjust the block state
        for (const key in nBlock.asset.behaviors) {
          behaviors[key]?.deriveStateFromNeighbours?.(this.behaviorWorldApi, ctx, nextState)
        }
        // if nothing changed, skip
        if (currState.toString() === nextState.toString()) continue
        // if state changed type, ignore it (prediction should only be adjustive, not destructive)
        if (currState.id !== nextState.id) continue
        // if it changed, modify it so neighbors "see" it
        nChunk.setStateAtIdx(nLoc.voxelIdx, nextState.toString())
        // add to changes
        changes.push({ loc: nLoc.voxel.toArray(), state: nextState.toString() })
        // now notify HIS neighbors
        checkNeighbors(nLoc)
      }
    }
    checkNeighbors(loc)
    // tell the chunk builder to rebuild all at once
    this.terrain.edit(changes)
    // send to server
    this.client.network.send('editorBreakBlock', {
      loc: loc.voxel.toArray(),
    })
  }

  editorPlaceBlock(hit, placerObj, blockId) {
    // no block? no place!
    if (!blockId) return
    const asset = this.client.registry.get('block', blockId)
    if (!asset) return
    // get location
    loc1.voxel.copy(hit.voxel)
    if (!hit.block.replaceable) {
      loc1.move(hit.direction)
    }
    // no chunk? stop
    const chunk = this.chunks.get(loc1.chunkId)
    if (!chunk) return
    // cant place a block if its not replaceable or its not air
    const block = chunk.getBlockAtIdx(loc1.voxelIdx)
    const canPlace = block.replaceable || block.id === 'air'
    if (!canPlace) return
    // cant place block if camera occupies voxel
    // loc2.world.copy(this.camera.position)
    // if (loc2.voxel.equals(loc1.voxel)) return
    //
    // update context
    this.placerCtx.set(loc1, hit.normal, placerObj)
    // start tracking changes
    const changes = []
    // resolve state change
    const nextState = bs1.fromString(blockId)
    for (const key in asset.behaviors) {
      behaviors[key]?.onBlockPlace?.(this.behaviorWorldApi, this.placerCtx, asset, nextState)
    }
    // track prediction edit
    changes.push({ loc: loc1.voxel.toArray(), state: nextState.toString() })
    // modify locally so neighbors "see" the data change
    chunk.setStateAtIdx(loc1.voxelIdx, nextState.toString())
    // allow neighbors to adjust (non-destructive)
    const checkNeighbors = loc => {
      for (const dir of Directions.all) {
        const nLoc = loc[dir.name]
        const nChunk = this.chunks.get(nLoc.chunkId)
        if (!nChunk) continue
        const nBlock = nChunk.getBlockAtIdx(nLoc.voxelIdx)
        if (!nBlock) continue
        const currState = bs1.fromString(nBlock.state)
        const nextState = bs2.fromString(nBlock.state)
        const ctx = {
          location: nLoc,
        }
        for (const key in nBlock.asset.behaviors) {
          behaviors[key]?.deriveStateFromNeighbours?.(this.behaviorWorldApi, ctx, nextState)
        }
        // if nothing changed, skip
        if (currState.toString() === nextState.toString()) continue
        // if state changed type, ignore it (prediction should only be adjustive, not destructive)
        if (currState.id !== nextState.id) continue
        // if it changed, modify it so neighbors "see" it
        nChunk.setStateAtIdx(nLoc.voxelIdx, nextState.toString())
        // add to changes
        changes.push({ loc: nLoc.voxel.toArray(), state: nextState.toString() })
        // now notify HIS neighbors
        checkNeighbors(nLoc)
      }
    }
    checkNeighbors(loc1)
    // tell the chunk builder to rebuild all at once
    this.terrain.edit(changes)
    // send to server
    this.client.network.send('editorPlaceBlock', {
      ctx: this.placerCtx.serialize(),
      loc: loc1.voxel.toArray(),
      blockId,
    })
  }

  getBlockAtLocation = loc => {
    const chunk = this.chunks.get(loc.chunkId)
    if (!chunk) return null
    const block = chunk.getBlockAtIdx(loc.voxelIdx)
    return block
  }

  onEditVoxels(changes) {
    // console.log('onEditVoxels', changes)
    // apply to local chunk data
    for (const change of changes) {
      loc1.voxel.fromArray(change.loc)
      const chunk = this.chunks.get(loc1.chunkId)
      chunk.setStateAtIdx(loc1.voxelIdx, change.state)
    }
    // send to builder for rebuild
    this.terrain.edit(changes)
  }

  onBlockDamage({ voxel, amount }) {
    // server provides us with authoritive block damage amount
    loc1.voxel.fromArray(voxel)
    const chunk = this.chunks.get(loc1.chunkId)
    if (!chunk) return
    // console.log('onBlockDamage', loc1.voxelId, amount)
    chunk.setDamageAt(loc1, amount)
  }
}
